import React, { useEffect } from 'react';
/** ############################ APOLLO ################################# */
import { useQuery } from '@apollo/client';
import { WORKSPACES_LIST } from '../../utils/apollo/workspaces';
import {
  IWorkspaceListQuery,
  IWorkspaceListQueryVariables,
} from '../../utils/apollo/graphqlSchema';
/** ############################ UTILS ################################## */
import { appRoutes } from '../../path';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
/** ############################# UI ################################### */
import EmptyMessage from './EmptyMessage';
import {
  populateWorkspaces,
  setWorkspaceLastVisitedPathname,
} from '../../redux/slices/workspaces';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useShowWorkspace } from '../../utils/hooks';

export default function Workspaces() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const urlParams = useParams();
  const lastVisitedPathname = useAppSelector(
    (state) => state.workspaces.lastVisitedWorkspacePathname,
  );
  const isWorkspaceEnabled = useShowWorkspace();

  // KBT-472 - redirect to dashboard if workspace is not enabled
  useEffect(() => {
    if (!isWorkspaceEnabled) {
      navigate(appRoutes.dashboard.routePrefix);
    }
  }, [isWorkspaceEnabled, navigate]);

  const redirectToWorkspace = ({ workspaceList }: IWorkspaceListQuery) => {
    if (workspaceList?.entities?.length) {
      dispatch(populateWorkspaces(workspaceList.entities));
      // in case user hit /workspaces without workspaceId, redirect to the workspace
      if (!urlParams.workspaceId) {
        // if lastVisitedPathname is set, redirect to that workspace
        // otherwise redirect to the first workspace
        if (lastVisitedPathname) {
          navigate(lastVisitedPathname);
          dispatch(
            setWorkspaceLastVisitedPathname({ lastVisitedPathname: null }),
          );
        } else {
          navigate(
            appRoutes.workspaces.workspace(workspaceList.entities[0].id),
          );
        }
      }
    }
  };

  /* Fetch the first n workspaces meta info only and then redirect to the first */
  const { data: workspacesData, loading: areWorkspacesLoading } = useQuery<
    IWorkspaceListQuery,
    IWorkspaceListQueryVariables
  >(WORKSPACES_LIST, {
    variables: {
      pagination: { after: 0, first: 50 },
      includes: [],
      term: '',
    },
    onCompleted: redirectToWorkspace,
  });

  // KBT-567 - hide workspaces page if feature flag is disabled
  if (!isWorkspaceEnabled) {
    return null;
  }

  if (
    !areWorkspacesLoading &&
    workspacesData?.workspaceList?.entities?.length === 0
  ) {
    return (
      <EmptyMessage
        imageUrl="/images/empty-workspace.svg"
        title="Start building your workspace"
        tip="To build your first workspace, you need to add some charts from your Report"
        buttonLabel="Go to Report"
        onButtonClick={() => navigate(appRoutes.report.query)}
      />
    );
  }
  return <Outlet />;
}
